import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoStoriesRoundedIcon from "@mui/icons-material/AutoStoriesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import Header from "../header_h/Header";
import Footer from "../footer/Footer";
import { getTextbooks } from "../../api/subjects";
import { modulesData } from "../author_page/modulesData";
import "./TextbooksPage.css";

const normalize = (value) => String(value ?? "").trim();
const getModuleCount = (textbook) => Math.max(
  Number(textbook.moduleCount) || 0,
  modulesData?.[textbook.classId]?.[textbook.subjectSlug]?.[textbook.authorSlug]?.length || 0
);
const formatModuleCount = (count) => {
  const value = Number(count) || 0;
  const lastTwo = value % 100;
  const last = value % 10;
  const word = last === 1 && lastTwo !== 11 ? "модуль" : last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? "модулі" : "модулів";
  return `${value} ${word}`;
};

function TextbooksPage({ userName, userRoles }) {
  const [textbooks, setTextbooks] = useState([]);
  const { classId: routeClassId } = useParams();
  const selectedClass = routeClassId || "all";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getTextbooks()
      .then((data) => { if (active) setTextbooks(Array.isArray(data) ? data : []); })
      .catch((e) => { if (active) setError(String(e?.response?.data?.message || e?.response?.data || e?.message || "Не вдалося завантажити підручники")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const classes = useMemo(() => [...new Set(textbooks.map((item) => Number(item.classId)).filter(Number.isFinite))].sort((a, b) => a - b), [textbooks]);
  const visibleTextbooks = useMemo(() => selectedClass === "all" ? textbooks : textbooks.filter((item) => Number(item.classId) === Number(selectedClass)), [selectedClass, textbooks]);

  return (
    <>
      <Header userName={userName} userRoles={userRoles} />
      <Box component="main" className="textbooks-page">
        <Container maxWidth="lg">
          <Box className="textbooks-page__heading">
            <Typography variant="h3" component="h1">Підручники</Typography>
            <Typography color="text.secondary">Оберіть підручник, щоб переглянути доступні електронні матеріали.</Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" className="textbooks-page__filters">
            <Button component={Link} to="/textbooks" variant={selectedClass === "all" ? "contained" : "outlined"}>Усі класи</Button>
            {classes.map((classId) => <Button component={Link} to={`/textbooks/${classId}`} key={classId} variant={Number(selectedClass) === classId ? "contained" : "outlined"}>{classId} клас</Button>)}
          </Stack>

          {loading && <Box className="textbooks-page__state"><CircularProgress /><Typography>Завантаження...</Typography></Box>}
          {error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && (
            <Box className="textbooks-grid">
              {visibleTextbooks.map((textbook) => {
                const route = `/class/${textbook.classId}/${textbook.subjectSlug}/${textbook.authorSlug}/modules`;
                return (
                  <Box key={`${textbook.classId}-${textbook.subjectSlug}-${textbook.authorSlug}`} sx={{ minWidth: 0 }}>
                    <Card className="textbook-card" elevation={0}>
                      <Box className="textbook-card__cover-wrap">
                        {normalize(textbook.coverImagePath) ? <Box component="img" className="textbook-card__cover" src={textbook.coverImagePath} alt={`Обкладинка: ${textbook.title}`} /> : (
                          <Box className="textbook-card__placeholder"><MenuBookRoundedIcon /><Typography title={textbook.subjectTitle}>{textbook.subjectTitle}</Typography><Typography variant="h4">{textbook.classId}</Typography><Typography variant="caption">клас</Typography></Box>
                        )}
                      </Box>
                      <CardContent className="textbook-card__content">
                        <Chip size="small" label={`${textbook.classId} клас`} />
                        <Typography variant="h6" component="h2" title={textbook.title}>{textbook.title}</Typography>
                        <Typography color="text.secondary" className="textbook-card__authors" title={textbook.authors}>{textbook.authors}</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="center" className="textbook-card__modules"><AutoStoriesRoundedIcon fontSize="small" /><Typography variant="body2">{formatModuleCount(getModuleCount(textbook))}</Typography></Stack>
                        <Button component={Link} to={route} variant="contained" fullWidth endIcon={<ArrowForwardRoundedIcon />}>Переглянути модулі</Button>
                      </CardContent>
                    </Card>
                  </Box>
                );
              })}
            </Box>
          )}
          {!loading && !error && visibleTextbooks.length === 0 && <Box className="textbooks-page__state"><Typography>Для цього класу підручників поки немає.</Typography></Box>}
        </Container>
      </Box>
      <Footer />
    </>
  );
}

export default TextbooksPage;
